import React, { useState, useRef } from 'react';
import { Send, Paperclip, Smile, AtSign, Bold, Italic, Code, Link, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SlackStyleInputProps {
  placeholder?: string;
  onSend: (content: string, files?: File[]) => void;
  disabled?: boolean;
}

export default function SlackStyleInput({ 
  placeholder = "Message #new-channel", 
  onSend, 
  disabled = false 
}: SlackStyleInputProps) {
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = () => {
    if (message.trim() || selectedFiles.length > 0) {
      onSend(message, selectedFiles);
      setMessage('');
      setSelectedFiles([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  const insertTextAtCursor = (before: string, after: string = '') => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = message.substring(start, end);
    
    const newText = message.substring(0, start) + before + selectedText + after + message.substring(end);
    setMessage(newText);
    
    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selectedText.length);
    }, 0);
  };

  const handleFormatting = (type: string) => {
    switch (type) {
      case 'bold':
        insertTextAtCursor('**', '**');
        break;
      case 'italic':
        insertTextAtCursor('*', '*');
        break;
      case 'code':
        insertTextAtCursor('`', '`');
        break;
      case 'link':
        insertTextAtCursor('[', '](url)');
        break;
      case 'mention':
        insertTextAtCursor('@');
        break;
      case 'emoji':
        insertTextAtCursor(':smile:');
        break;
    }
  };

  return (
    <div className="border border-slate-600 rounded-lg bg-slate-800/50 backdrop-blur-sm">
      {/* File attachments preview */}
      {selectedFiles.length > 0 && (
        <div className="p-3 border-b border-slate-600">
          <div className="flex flex-wrap gap-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center gap-2 bg-slate-700 rounded px-2 py-1 text-xs">
                <Paperclip className="w-3 h-3" />
                <span className="text-slate-300">{file.name}</span>
                <span className="text-slate-500">({formatFileSize(file.size)})</span>
                <button
                  onClick={() => removeFile(index)}
                  className="text-slate-400 hover:text-white ml-1"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Formatting toolbar */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-600/50">
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
          onClick={() => handleFormatting('bold')}
          title="Bold"
        >
          <Bold className="w-3 h-3" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
          onClick={() => handleFormatting('italic')}
          title="Italic"
        >
          <Italic className="w-3 h-3" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
          onClick={() => handleFormatting('code')}
          title="Code"
        >
          <Code className="w-3 h-3" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
          onClick={() => handleFormatting('link')}
          title="Link"
        >
          <Link className="w-3 h-3" />
        </Button>
        
        <div className="h-4 w-px bg-slate-600 mx-1" />
        
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
          onClick={() => handleFormatting('mention')}
          title="Mention"
        >
          <AtSign className="w-3 h-3" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
          onClick={() => handleFormatting('emoji')}
          title="Emoji"
        >
          <Smile className="w-3 h-3" />
        </Button>
      </div>

      {/* Input area */}
      <div className="flex items-end gap-2 p-3">
        <div className="flex-1">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleTextareaChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full bg-transparent border-0 outline-none resize-none text-slate-200 placeholder-slate-500 min-h-[24px] max-h-32"
            rows={1}
          />
        </div>
        
        <div className="flex items-center gap-1">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*,video/*,.pdf,.doc,.docx,.txt"
          />
          
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-700 transition-all duration-200 hover:scale-105"
            onClick={() => {
              console.log('📎 File attachment button clicked');
              fileInputRef.current?.click();
            }}
            disabled={disabled}
            title="Attach files"
          >
            <Paperclip className="w-4 h-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
            disabled={disabled}
          >
            <Plus className="w-4 h-4" />
          </Button>
          
          <Button
            onClick={handleSend}
            disabled={disabled || (!message.trim() && selectedFiles.length === 0)}
            className="h-8 w-8 p-0 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 disabled:text-slate-500 transition-all duration-200 hover:scale-105"
            title="Send message"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}